import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Sparkles, LayoutGrid, Layers, ArrowDownToLine, Tag } from 'lucide-react';
import { WIDGET_CATEGORIES, WIDGET_DEFINITIONS } from './widgets/widgetRegistry';

export default function WidgetLibrary({ onAddWidget, onAddSection, onInsertSavedBlock, client }) {
  const [activeTab, setActiveTab] = useState('elements'); // 'elements' or 'saved_cards'
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [savedBlocks, setSavedBlocks] = useState([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);

  useEffect(() => {
    if (activeTab === 'saved_cards' && client) {
      setLoadingBlocks(true);
      client
        .get('/cms/page-blocks', { params: { page: 1, limit: 100, sort_by: 'display_order', sort_order: 'asc' } })
        .then((res) => {
          setSavedBlocks(res.data.items || []);
        })
        .catch(() => setSavedBlocks([]))
        .finally(() => setLoadingBlocks(false));
    }
  }, [activeTab, client]);

  const filteredWidgets = useMemo(() => {
    return Object.values(WIDGET_DEFINITIONS).filter((widget) => {
      const matchesCat = activeCategory === 'all' || widget.category === activeCategory;
      const matchesSearch =
        !search ||
        widget.name.toLowerCase().includes(search.toLowerCase()) ||
        widget.description.toLowerCase().includes(search.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [search, activeCategory]);

  const filteredBlocks = useMemo(() => {
    if (!search) return savedBlocks;
    const s = search.toLowerCase();
    return savedBlocks.filter(
      (b) =>
        (b.title || '').toLowerCase().includes(s) ||
        (b.page_slug || '').toLowerCase().includes(s) ||
        (b.eyebrow || '').toLowerCase().includes(s) ||
        (b.description || '').toLowerCase().includes(s)
    );
  }, [savedBlocks, search]);

  return (
    <div className="flex flex-col h-full bg-white select-none">
      {/* Header with Search & Quick Action */}
      <div className="p-4 border-b border-slate-200 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <LayoutGrid className="w-3.5 h-3.5 text-[#2b3380]" />
            <span>Element Library</span>
          </h2>
          <button
            type="button"
            onClick={() => onAddSection && onAddSection()}
            className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-[#2b3380] rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors"
            title="Append a new full-width section to the canvas"
          >
            <Plus className="w-3 h-3" />
            <span>+ Section</span>
          </button>
        </div>

        {/* Main Tab Switcher: Standard Elements vs Saved Cards */}
        <div className="flex bg-slate-100 p-1 rounded-xl text-xs font-semibold">
          <button
            type="button"
            onClick={() => setActiveTab('elements')}
            className={`flex-1 py-1.5 rounded-lg text-center transition-all ${
              activeTab === 'elements'
                ? 'bg-white text-[#2b3380] shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            🧩 Elements
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('saved_cards')}
            className={`flex-1 py-1.5 rounded-lg text-center transition-all flex items-center justify-center gap-1 ${
              activeTab === 'saved_cards'
                ? 'bg-white text-[#2b3380] shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>📦</span>
            <span>Saved Cards</span>
          </button>
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={activeTab === 'elements' ? 'Search elements...' : 'Search saved cards & blocks...'}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2b3380] focus:bg-white transition"
          />
        </div>

        {/* Category Pills (for Elements tab) */}
        {activeTab === 'elements' && (
          <div className="flex gap-1 overflow-x-auto pb-1 text-[11px] scrollbar-none">
            <button
              type="button"
              onClick={() => setActiveCategory('all')}
              className={`px-2.5 py-1 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                activeCategory === 'all'
                  ? 'bg-[#2b3380] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All
            </button>
            {WIDGET_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`px-2.5 py-1 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-[#2b3380] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat.label.replace(' Widgets', '')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'saved_cards' ? (
          /* SAVED CARDS & BLOCKS VIEW */
          loadingBlocks ? (
            <div className="text-center py-12 text-slate-400 text-xs">
              <div className="w-5 h-5 border-2 border-[#2b3380] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p>Loading saved cards...</p>
            </div>
          ) : filteredBlocks.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs space-y-2">
              <Layers className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="font-semibold text-slate-600">No saved cards found</p>
              <p className="text-[11px]">Cards created in CMS or other pages will appear here for 1-click reuse.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-[11px] text-slate-500 font-medium">
                Click <strong>+ Insert</strong> to add any existing card directly to this page:
              </p>
              {filteredBlocks.map((block) => (
                <div
                  key={block.id || block.title}
                  className="p-3 rounded-xl border border-slate-200 hover:border-[#2b3380] bg-white shadow-2xs hover:shadow-xs transition-all space-y-2"
                >
                  <div className="flex items-start gap-2.5">
                    {block.image ? (
                      <img
                        src={block.image}
                        alt=""
                        className="w-12 h-12 object-contain rounded-lg border bg-slate-50 flex-shrink-0 p-1"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-700 flex-shrink-0">
                        <Layers className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {block.eyebrow && (
                        <span className="text-[10px] font-bold text-[#2b3380] uppercase tracking-wider block truncate">
                          {block.eyebrow}
                        </span>
                      )}
                      <h4 className="text-xs font-bold text-slate-900 truncate" title={block.title}>
                        {block.title || 'Untitled Card'}
                      </h4>
                      {block.description && (
                        <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-snug">
                          {block.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded font-mono">
                      From: /{block.page_slug || 'general'}
                    </span>
                    <button
                      type="button"
                      onClick={() => onInsertSavedBlock && onInsertSavedBlock(block)}
                      className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 shadow-2xs cursor-pointer active:scale-[0.97] transition-all"
                    >
                      <Plus className="w-3 h-3" />
                      <span>+ Insert</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* STANDARD ELEMENTS GRID */
          filteredWidgets.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs">
              <p>No elements match &ldquo;{search}&rdquo;</p>
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setActiveCategory('all');
                }}
                className="mt-2 text-[#2b3380] font-semibold underline"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {filteredWidgets.map((widget) => {
                const Icon = widget.icon;
                return (
                  <button
                    key={widget.type}
                    type="button"
                    onClick={() => onAddWidget && onAddWidget(widget.type)}
                    className="p-3 rounded-xl border border-slate-200 hover:border-[#2b3380] hover:bg-indigo-50/40 bg-white text-left transition-all duration-150 flex flex-col justify-between group shadow-2xs hover:shadow-xs"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="p-2 rounded-lg bg-slate-100 text-slate-600 group-hover:bg-[#2b3380] group-hover:text-white transition-colors">
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-[#2b3380]">
                        + Add
                      </span>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800 group-hover:text-[#2b3380] mb-0.5">
                        {widget.name}
                      </div>
                      <p className="text-[11px] text-slate-500 line-clamp-2 leading-snug">
                        {widget.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
